// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/**
 * Pure geometry + planning helpers for redaction. No DOM, no pdf.js — all of
 * this is unit-tested. The rest of the app builds on these primitives so the
 * fiddly, safety-critical maths lives in one testable place.
 */

import type { Box } from './types';

/** Clamp a number into [lo, hi]. */
export function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo;
  return n < lo ? lo : n > hi ? hi : n;
}

let boxSeq = 0;
/** A short, collision-resistant id for a box (deterministic ordering, no RNG). */
export function nextBoxId(): string {
  boxSeq += 1;
  return `b${boxSeq.toString(36)}`;
}

/**
 * Build a normalized box from two drag points given in *pixel* coordinates on
 * a surface of size (w, h). Points may be in any order. The result is clamped
 * to the page and returned in 0..1 space.
 */
export function boxFromDrag(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  w: number,
  h: number,
): Box {
  const left = clamp(Math.min(x0, x1) / w, 0, 1);
  const top = clamp(Math.min(y0, y1) / h, 0, 1);
  const right = clamp(Math.max(x0, x1) / w, 0, 1);
  const bottom = clamp(Math.max(y0, y1) / h, 0, 1);
  return {
    id: nextBoxId(),
    x: left,
    y: top,
    w: Math.max(0, right - left),
    h: Math.max(0, bottom - top),
  };
}

/** Is this box large enough to keep? (Guards against accidental clicks.) */
export function isMeaningful(box: Box, minFrac = 0.004): boolean {
  return box.w >= minFrac && box.h >= minFrac;
}

/**
 * Grow a box outward by `padFrac` of the page on every side, clamped to the
 * page. Redaction should slightly over-cover so no glyph edge peeks out from
 * under the bar — over-covering is the safe failure mode.
 */
export function padBox(box: Box, padFrac: number): Box {
  const x = clamp(box.x - padFrac, 0, 1);
  const y = clamp(box.y - padFrac, 0, 1);
  const right = clamp(box.x + box.w + padFrac, 0, 1);
  const bottom = clamp(box.y + box.h + padFrac, 0, 1);
  return { ...box, x, y, w: Math.max(0, right - x), h: Math.max(0, bottom - y) };
}

export interface PixelRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Convert a normalized box to an integer pixel rect on a (w, h) surface. */
export function boxToPixels(box: Box, w: number, h: number): PixelRect {
  const x = Math.round(clamp(box.x, 0, 1) * w);
  const y = Math.round(clamp(box.y, 0, 1) * h);
  const right = Math.round(clamp(box.x + box.w, 0, 1) * w);
  const bottom = Math.round(clamp(box.y + box.h, 0, 1) * h);
  return { x, y, w: Math.max(0, right - x), h: Math.max(0, bottom - y) };
}

/** Does the point (px, py), in normalized space, fall inside the box? */
export function pointInBox(box: Box, px: number, py: number): boolean {
  return px >= box.x && px <= box.x + box.w && py >= box.y && py <= box.y + box.h;
}

/**
 * Given a map of page-index → boxes and the total page count, decide which
 * pages must be flattened to an image. A page is flattened if it carries at
 * least one meaningful box, or if `flattenAll` is set.
 */
export function planFlatten(
  boxesByPage: Map<number, Box[]>,
  pageCount: number,
  flattenAll: boolean,
): number[] {
  const out: number[] = [];
  for (let i = 0; i < pageCount; i++) {
    if (flattenAll) {
      out.push(i);
      continue;
    }
    const boxes = boxesByPage.get(i);
    if (boxes && boxes.some((b) => isMeaningful(b))) out.push(i);
  }
  return out;
}

/** Total number of meaningful redaction boxes across all pages. */
export function countBoxes(boxesByPage: Map<number, Box[]>): number {
  let n = 0;
  for (const boxes of boxesByPage.values()) {
    for (const b of boxes) if (isMeaningful(b)) n++;
  }
  return n;
}

/** Render scale (canvas px per PDF point) for a target DPI. */
export function scaleForDpi(dpi: number): number {
  return clamp(dpi, 36, 400) / 72;
}

/** Derive the output filename from the input name. */
export function deriveOutName(input: string | undefined): string {
  const base = (input || 'document').replace(/\.[^.]+$/, '').trim() || 'document';
  return `${base}-redacted.pdf`;
}

/** True if the bytes start with the %PDF- magic marker. */
export function looksLikePdf(bytes: ArrayBuffer): boolean {
  if (bytes.byteLength < 5) return false;
  const head = new Uint8Array(bytes.slice(0, 5));
  return (
    head[0] === 0x25 && // %
    head[1] === 0x50 && // P
    head[2] === 0x44 && // D
    head[3] === 0x46 && // F
    head[4] === 0x2d // -
  );
}

/**
 * Find every case-insensitive occurrence of `needle` in `haystack`, returning
 * [start, end) character ranges. Overlapping matches are not returned (search
 * advances past each hit). Empty/whitespace needles return nothing.
 */
export function findRanges(haystack: string, needle: string): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  const term = needle.trim();
  if (!term) return out;
  const hay = haystack.toLowerCase();
  const nee = term.toLowerCase();
  let from = 0;
  for (;;) {
    const at = hay.indexOf(nee, from);
    if (at === -1) break;
    out.push([at, at + nee.length]);
    from = at + nee.length;
  }
  return out;
}
